import { useEffect } from "react";
import { useQuery } from "react-query";
import { getCountries, getCountrySetting } from "../queries/admin";
import useNetworkHeaders from "./useNetworkHeaders";

interface IUseCountries {
  onLoaded?: () => void;
}

/**
 * Retrieve country data
 *
 * @returns {Object} countries - Unaltered country data
 * @returns {Object} countriesMap - Mapping of country data by ID (ISO 2 code)
 * @returns {Object} countryDropdownOptions - Country data formatted for dropdown
 */
export default function useCountries({ onLoaded }: IUseCountries = {}) {
  const networkHeaders = useNetworkHeaders();

  // Retrieve country data
  const countriesQuery = useQuery(["countries"], getCountries);
  const countriesLoading = countriesQuery.isLoading;

  // Retrieve agency's country
  const agencyCountryQuery = useQuery(["agencyCountry"], getCountrySetting(networkHeaders));

  useEffect(() => {
    if (countriesQuery.status === "success" && agencyCountryQuery.status === "success") {
      if (onLoaded) {
        onLoaded();
      }
    }
  }, [countriesQuery, agencyCountryQuery]);

  const agencyCountryLoading = agencyCountryQuery.isLoading;
  if (!agencyCountryQuery.data || !countriesQuery.data) {
    return {
      countries: {} as { [key: string]: any },
      countryDropdownOptions: [],
      agencyCountry: null,
      error: "Failed to retrieve country data",
      countriesLoading: countriesLoading,
      agencyCountryLoading: agencyCountryLoading,
    };
  }

  // Unaltered country data
  const countries: {
    [key: string]: {
      id: string;
      countryName: string;
      callingCode: string;
      iso3: string;
      postalFirst: boolean;
      hasStateInAddress: boolean;
      postalFormat: string | null;
      states: string[] | null;
    };
  } = countriesQuery.data;

  // Formatted for dropdown
  const initialCountryDropdownOptions = Object.values(countries).map((country) => ({
    key: country.id,
    value: country.id,
    text: country.countryName,
  }));

  // Shift US to the front of the dropdown options
  const countryDropdownOptions = [
    initialCountryDropdownOptions.find((c) => c.key === "US"),
    ...initialCountryDropdownOptions.filter((c) => c.key !== "US"),
  ] as {
    key: string;
    value: string;
    text: string;
  }[];

  // Agency country data
  const agencyCountry = agencyCountryQuery.data;

  return { countries, countryDropdownOptions, agencyCountry, countriesLoading, agencyCountryLoading };
}
